import { KARAOKE_TRACK_NUMBER } from '@src/stores/VideoCreatorStore';

import type { ElementState } from '@src/renderer/ElementState';

/**
 * Interface for the VideoCreatorStore methods needed by track type utilities.
 * This allows both VideoCreatorStore and IVideoCreatorStore to be used.
 */
interface VideoCreatorLike {
  isOriginalVideoElement(source: unknown): boolean;
  isLogoElement(element: ElementState): boolean;
  isImageElementComposition(element: ElementState): boolean;
  isVideoElementComposition(element: ElementState): boolean;
}

/**
 * Type guard to check if the videoCreator has required methods.
 * IVideoCreatorStore uses index signature escape hatch so methods exist at runtime.
 */
function hasTrackTypeMethods(vc: unknown): vc is VideoCreatorLike {
  if (vc == null || typeof vc !== 'object') return false;
  // Runtime duck-typing check - necessary for IVideoCreatorStore compatibility
  // eslint-disable-next-line no-restricted-syntax
  const obj = vc as Record<string, unknown>;
  return (
    typeof obj.isOriginalVideoElement === 'function' &&
    typeof obj.isLogoElement === 'function' &&
    typeof obj.isImageElementComposition === 'function' &&
    typeof obj.isVideoElementComposition === 'function'
  );
}

/**
 * Track types for drag-and-drop compatibility checking.
 *
 * Track classification:
 * - `original-video`: The main video track (isOriginalVideo prop = true)
 * - `linked-audio`: Audio portion of original video (type prop = 'linked-audio')
 * - `video`: B-roll/overlay video tracks
 * - `audio`: Music/sound effect tracks
 * - `image`: Photo tracks (includes image compositions)
 * - `composition`: Graphics compositions (not image/video compositions)
 * - `text`: Text element tracks
 * - `karaoke`: Karaoke/caption tracks (distinct from text)
 * - `shape`: Shape element tracks
 * - `logo`: Logo element tracks
 * - `scenes`: Scene boundary track (not droppable)
 */
export type TrackType =
  | 'original-video'
  | 'linked-audio'
  | 'scenes'
  | 'text'
  | 'karaoke'
  | 'shape'
  | 'composition'
  | 'logo'
  | 'audio'
  | 'video'
  | 'image';

/**
 * Style flags derived from TrackType for component styling.
 */
export interface TrackStyleFlags {
  isOriginalVideo: boolean;
  isLinkedAudio: boolean;
  isScenesTrack: boolean;
  isText: boolean;
  isKaraoke: boolean;
  isShape: boolean;
  isComposition: boolean;
  isLogo: boolean;
  isAudio: boolean;
  isVideo: boolean;
  isImage: boolean;
}

/**
 * Derives styling flags from a TrackType.
 * Use this in components to get boolean flags for conditional styling.
 */
export function getTrackStyleFlags(trackType: TrackType): TrackStyleFlags {
  return {
    isOriginalVideo: trackType === 'original-video',
    isLinkedAudio: trackType === 'linked-audio',
    isScenesTrack: trackType === 'scenes',
    isText: trackType === 'text',
    isKaraoke: trackType === 'karaoke',
    isShape: trackType === 'shape',
    isComposition: trackType === 'composition',
    isLogo: trackType === 'logo',
    isAudio: trackType === 'audio',
    isVideo: trackType === 'video',
    isImage: trackType === 'image',
  };
}

/**
 * Derives the track type for a track based on its elements.
 * This is the primary function for determining track type.
 */
export function getTrackType(
  elements: ElementState[],
  videoCreator: unknown,
): TrackType {
  const firstElement = elements[0];
  const firstSource = firstElement?.source;

  if (!firstSource) return 'video'; // Default fallback

  // Check if videoCreator has required methods
  const hasVc = hasTrackTypeMethods(videoCreator);

  // Original video track
  if (hasVc && videoCreator.isOriginalVideoElement(firstSource)) {
    return 'original-video';
  }

  // Logo track (check before image since logos are specialized images)
  if (hasVc && elements.some(e => videoCreator.isLogoElement(e))) {
    return 'logo';
  }

  // Karaoke track (distinct from text)
  if (firstSource.track === KARAOKE_TRACK_NUMBER) {
    return 'karaoke';
  }

  // Graphics composition (not image/video composition)
  if (firstSource.type === 'composition') {
    if (hasVc && videoCreator.isImageElementComposition(firstElement)) {
      return 'image';
    }
    if (hasVc && videoCreator.isVideoElementComposition(firstElement)) {
      return 'video';
    }
    return 'composition';
  }

  // Direct type mapping
  const typeMap: Partial<Record<string, TrackType>> = {
    audio: 'audio',
    video: 'video',
    image: 'image',
    text: 'text',
    shape: 'shape',
  };

  return typeMap[firstSource.type] || 'video';
}

/**
 * Derives the track type for a draggable element.
 * This determines which track types the element can be dropped onto.
 */
export function getElementTrackType(
  element: ElementState,
  videoCreator: unknown,
  /** The track type from the parent track, if known */
  parentTrackType?: TrackType,
): TrackType {
  // If parent track type is provided and is linked-audio, use it
  if (parentTrackType === 'linked-audio') return 'linked-audio';

  // Ensure videoCreator has required methods
  if (!hasTrackTypeMethods(videoCreator)) {
    // Fallback to basic type mapping if methods unavailable
    const basicTypeMap: Partial<Record<string, TrackType>> = {
      audio: 'audio',
      video: 'video',
      image: 'image',
      text: 'text',
      shape: 'shape',
      composition: 'composition',
    };
    return basicTypeMap[element.source.type] || 'video';
  }

  // Original video element
  if (videoCreator.isOriginalVideoElement(element.source)) {
    return 'original-video';
  }

  // Logo element (specialized image)
  if (videoCreator.isLogoElement(element)) {
    return 'logo';
  }

  // Karaoke track elements (distinct from text)
  if (element.source.track === KARAOKE_TRACK_NUMBER) {
    return 'karaoke';
  }

  // Composition handling - check subtype first
  if (element.source.type === 'composition') {
    if (videoCreator.isImageElementComposition(element)) {
      return 'image';
    }
    if (videoCreator.isVideoElementComposition(element)) {
      return 'video';
    }
    // Graphics composition (not image/video)
    return 'composition';
  }

  // Direct type mapping
  const typeMap: Partial<Record<string, TrackType>> = {
    audio: 'audio',
    video: 'video',
    image: 'image',
    text: 'text',
    shape: 'shape',
  };

  return typeMap[element.source.type] || 'video';
}

/**
 * Compatibility rules for drag-and-drop between track types.
 *
 * Rules:
 * - Same type → always compatible
 *
 * Non-droppable tracks:
 * - scenes, linked-audio, original-video: Elements cannot be dropped here
 */
export function isCompatibleTrackType(
  elementType: TrackType,
  targetType: TrackType | null | undefined,
): boolean {
  if (!targetType) return false;

  const nonDroppable: TrackType[] = [
    'scenes',
    'linked-audio',
    'original-video',
  ];
  if (nonDroppable.includes(targetType)) return false;

  if (targetType === elementType) return true;

  return false;
}
